import { StatCard } from "@/components/metrics/stat-card";
import { api } from "@/modules/api";
import { Button } from "@/components/button";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { AlertTriangle, RefreshCcw, UserCircle2 } from "lucide-react";
import { FC, useMemo } from "react";

const LINEAR_TEAM_SLUG = "mem-labs";

const getOpenCount = (total?: number | null, done?: number | null, inReview?: number | null) => {
  const safeTotal = Math.max(0, total ?? 0);
  const safeDone = Math.max(0, done ?? 0);
  const safeInReview = Math.max(0, inReview ?? 0);

  return Math.max(0, safeTotal - safeDone - safeInReview);
};

export const AdminLinearDashboardRootPage: FC = () => {
  const result = useQuery({
    queryKey: ["linear-dashboard"],
    queryFn: () => api.get("/internal/v1/linear/overview"),
    staleTime: 30_000,
  });

  const overview = result.data?.data;

  const lastUpdatedDisplay = overview?.last_updated_at
    ? DateTime.fromISO(overview.last_updated_at).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
    : "—";

  const urgentIssues = useMemo(() => {
    const issues = overview?.urgent_issues ?? [];
    const normalizeStatus = (value?: string | null) => value?.trim().toLowerCase();
    const statusOrder = ["in progress", "pending", "todo"];

    return issues
      .filter(issue => {
        const status = normalizeStatus(issue.state_name);
        return status !== "done" && status !== "in review" && status !== "canceled";
      })
      .map((issue, index) => ({ issue, index }))
      .sort((a, b) => {
        const rankFor = (value: typeof a) => {
          const status = normalizeStatus(value.issue.state_name) ?? "";
          const rank = statusOrder.indexOf(status);
          return rank === -1 ? statusOrder.length : rank;
        };

        const rankDiff = rankFor(a) - rankFor(b);
        if (rankDiff !== 0) {
          return rankDiff;
        }

        return a.index - b.index;
      })
      .map(({ issue }) => issue);
  }, [overview?.urgent_issues]);

  const completionStats = useMemo(() => {
    const normalizeCounts = (done?: number | null, total?: number | null) => {
      const safeTotal = Math.max(0, total ?? 0);
      const safeDone = Math.max(0, Math.min(done ?? 0, safeTotal));

      if (safeTotal === 0) {
        return { percent: 100, label: "100% done" };
      }

      const percent = Math.round((safeDone / safeTotal) * 100);
      return { percent, label: `${percent}% done` };
    };

    return {
      urgent: normalizeCounts(overview?.urgent_done, overview?.urgent_count),
      high: normalizeCounts(overview?.high_done, overview?.high_count),
      medium: normalizeCounts(overview?.medium_done, overview?.medium_count),
    };
  }, [
    overview?.high_count,
    overview?.high_done,
    overview?.medium_count,
    overview?.medium_done,
    overview?.urgent_count,
    overview?.urgent_done,
  ]);

  const cycleLabel = useMemo(() => {
    if (!overview) {
      return "No active cycle";
    }

    const pieces = [overview.cycle_name ?? overview.cycle_number?.toString()].filter(Boolean);
    return pieces.length > 0 ? pieces.join(" • ") : "Active cycle";
  }, [overview]);

  if (result.isLoading) {
    return <StateMessage>Loading Linear dashboard…</StateMessage>;
  }

  if (result.isError) {
    const error = result.error as { message?: string; data?: { detail?: string } };
    const errorMessage =
      error?.data?.detail ?? error?.message ?? "Unable to load Linear dashboard.";

    return (
      <StateMessage>
        <AlertTriangle size={18} />
        <span>{errorMessage}</span>
        <Button variant="outline" size="1" onClick={() => result.refetch()}>
          <ButtonContent>
            <RefreshCcw size={14} />
            <span>Retry</span>
          </ButtonContent>
        </Button>
      </StateMessage>
    );
  }

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <PageTitle>Linear Dashboard</PageTitle>
          <Subtitle>
            {overview?.team_name ?? "Linear"} • {cycleLabel}
          </Subtitle>
        </div>
        <Actions>
          <LastUpdated>Last updated: {lastUpdatedDisplay}</LastUpdated>
          <Button variant="outline" size="1" onClick={() => result.refetch()}>
            <ButtonContent>
              <RefreshCcw size={14} />
              <span>Refresh</span>
            </ButtonContent>
          </Button>
        </Actions>
      </HeaderRow>

      <StatsGrid>
        <StatCard
          label="Urgent"
          value={getOpenCount(
            overview?.urgent_count,
            overview?.urgent_done,
            overview?.urgent_in_review
          )}
          tone="danger"
          description={`${overview?.urgent_in_review ?? 0} in review • ${overview?.urgent_done ?? 0} done`}
          icon={<AlertTriangle size={16} color="#d92d20" />}
          progressPercent={completionStats.urgent.percent}
          progressLabel={completionStats.urgent.label}
        />
        <StatCard
          label="High"
          value={getOpenCount(overview?.high_count, overview?.high_done, overview?.high_in_review)}
          tone="warning"
          description={`${overview?.high_in_review ?? 0} in review • ${overview?.high_done ?? 0} done`}
          progressPercent={completionStats.high.percent}
          progressLabel={completionStats.high.label}
        />
        <StatCard
          label="Medium"
          value={getOpenCount(
            overview?.medium_count,
            overview?.medium_done,
            overview?.medium_in_review
          )}
          description={`${overview?.medium_in_review ?? 0} in review • ${overview?.medium_done ?? 0} done`}
          progressPercent={completionStats.medium.percent}
          progressLabel={completionStats.medium.label}
        />
      </StatsGrid>

      <UrgentIssuesCard>
        <SectionTitle>Urgent issues</SectionTitle>
        {urgentIssues.length === 0 ? (
          <EmptyState>No urgent issues in the current cycle. 🎉</EmptyState>
        ) : (
          <IssuesList>
            {urgentIssues.map(issue => {
              const issueUrl = issue.identifier
                ? `https://linear.app/${LINEAR_TEAM_SLUG}/issue/${issue.identifier}`
                : undefined;

              return (
                <IssueRow key={issue.id}>
                  <IssueIdentifier>
                    {issueUrl ? (
                      <a href={issueUrl} target="_blank" rel="noreferrer">
                        {issue.identifier}
                      </a>
                    ) : (
                      (issue.identifier ?? "—")
                    )}
                  </IssueIdentifier>
                  <IssueTitle>{issue.title}</IssueTitle>
                  <IssueMeta>
                    {issue.assignee_names?.length ? (
                      <IssueAssignees>
                        <UserCircle2 size={14} />
                        <span>{issue.assignee_names.join(", ")}</span>
                      </IssueAssignees>
                    ) : null}
                    {issue.state_name ? <IssueState>{issue.state_name}</IssueState> : null}
                  </IssueMeta>
                </IssueRow>
              );
            })}
          </IssuesList>
        )}
      </UrgentIssuesCard>

      <FooterNote>
        Need something deeper? Explore Atlas tools or open Linear directly via the links above.
      </FooterNote>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: var(--gray-11);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LastUpdated = styled.span`
  color: var(--gray-11);
  font-size: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const UrgentIssuesCard = styled.div`
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  padding: 20px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IssueRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: 12px;
  align-items: center;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 6px;
    align-items: flex-start;
  }
`;

const IssueIdentifier = styled.div`
  font-weight: 600;

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const IssueTitle = styled.div`
  color: var(--gray-12);
`;

const IssueMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IssueState = styled.span`
  font-size: 12px;
  background: var(--gray-3);
  color: var(--gray-12);
  padding: 4px 8px;
  border-radius: 999px;
  text-transform: capitalize;
`;

const IssueAssignees = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  background: var(--gray-3);
  color: var(--gray-12);
  padding: 4px 8px;
  border-radius: 999px;

  svg {
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  color: var(--gray-11);
`;

const FooterNote = styled.p`
  margin-top: 8px;
  color: var(--gray-10);
  font-size: 12px;
`;

const StateMessage = styled.div`
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--gray-11);

  svg {
    flex-shrink: 0;
  }

  button {
    margin-left: 8px;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
